import { useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Zap } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";

const NAV_LINKS = [
  { label: "Home", path: "/" },
  { label: "Solar Panels", path: "/highest-efficiency-solar-panels" },
  { label: "Best Panels", path: "/best-high-efficiency-solar-panels" },
  { label: "Calculator", path: "/solar-panel-calculator" },
  { label: "Reviews", path: "/reviews" },
];

const GUIDE_LINKS = [
  { label: "All Solar Guides", path: "/solar-guides" },
  { label: "Solar Cell vs Solar Panel", path: "/solar-cell-vs-solar-panel" },
  { label: "Mono vs Poly", path: "/monocrystalline-vs-polycrystalline-solar-panels" },
  { label: "Bifacial Panels", path: "/bifacial-solar-panels" },
  { label: "Flexible Panels", path: "/flexible-solar-panels" },
  { label: "Best for RV", path: "/best-solar-panels-for-rv" },
  { label: "Best Portable", path: "/best-portable-solar-panels" },
  { label: "Best for Solar Generators", path: "/best-solar-panels-for-solar-generators" },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [guidesOpen, setGuidesOpen] = useState(false);
  const guidesRef = useRef(null);
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const isAdmin = isAuthenticated && user?.role === "admin";
  const isActive = (path) => location.pathname === path;

  return (
    <nav className="sticky top-0 z-50 bg-background/90 backdrop-blur-xl border-b border-border shadow-sm">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 group shrink-0">
            <div className="w-8 h-8 rounded-lg bg-accent flex items-center justify-center shadow-md" style={{ boxShadow: '0 0 12px rgba(250,185,0,0.35)' }}>
              <Zap className="w-4.5 h-4.5 text-slate-950" strokeWidth={2.5} />
            </div>
            <span className="font-heading font-bold text-base hidden sm:block tracking-tight">
              HighestEfficiencySolarCell
            </span>
            <span className="font-heading font-bold text-base sm:hidden tracking-tight">HESC</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-0.5">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-all ${
                  isActive(link.path)
                    ? "bg-accent text-slate-950 font-semibold"
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div
              ref={guidesRef}
              className="relative"
              onMouseEnter={() => setGuidesOpen(true)}
              onMouseLeave={() => setGuidesOpen(false)}
            >
              <button
                className="px-3.5 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary flex items-center gap-1.5 transition-all"
                onClick={() => setGuidesOpen((v) => !v)}
                aria-expanded={guidesOpen}
                aria-haspopup="true"
              >
                Guides <ChevronDown className={`w-3.5 h-3.5 opacity-60 transition-transform ${guidesOpen ? "rotate-180" : ""}`} />
              </button>
              {guidesOpen && (
                <div className="absolute right-0 top-full mt-1 w-64 bg-card border border-border rounded-xl shadow-xl py-1 z-50">
                  {GUIDE_LINKS.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setGuidesOpen(false)}
                      className="block px-4 py-2.5 text-sm text-foreground hover:bg-secondary transition-colors"
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            {isAdmin && (
              <Link
                to="/admin/product-reviews"
                className="px-3.5 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-all"
              >
                Moderate Reviews
              </Link>
            )}
            <Link
              to="/best-high-efficiency-solar-panels"
              className="ml-2 px-4 py-2 bg-accent text-slate-950 font-heading font-bold text-sm rounded-lg hover:bg-amber-400 transition-all min-h-[36px] flex items-center"
            >
              Compare Panels
            </Link>
          </div>

          {/* Mobile Toggle */}
          <button
            className="lg:hidden p-2 rounded-lg hover:bg-secondary transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-border bg-background/98 backdrop-blur-xl">
          <div className="px-4 py-4 space-y-1">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileOpen(false)}
                className={`block px-3.5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive(link.path)
                    ? "bg-accent text-slate-950 font-semibold"
                    : "text-muted-foreground hover:bg-secondary"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-2 border-t border-border mt-2">
              <p className="px-3.5 py-1 text-[10px] font-mono text-muted-foreground uppercase tracking-widest mb-1">Buyer Guides</p>
              {GUIDE_LINKS.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileOpen(false)}
                  className="block px-3.5 py-2.5 rounded-lg text-sm text-muted-foreground hover:bg-secondary transition-colors"
                >
                  {link.label}
                </Link>
              ))}
            </div>
            {isAdmin && (
              <Link
                to="/admin/product-reviews"
                onClick={() => setMobileOpen(false)}
                className="block px-3.5 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-secondary transition-colors"
              >
                Moderate Reviews
              </Link>
            )}
            <div className="pt-2">
              <Link
                to="/best-high-efficiency-solar-panels"
                onClick={() => setMobileOpen(false)}
                className="block text-center px-4 py-3 bg-accent text-slate-950 font-heading font-bold text-sm rounded-xl hover:bg-amber-400 transition-all"
              >
                Compare High-Efficiency Panels →
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}